import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext'; 
import { 
  TrendingUp, 
  TrendingDown, 
  BarChart2, 
  RefreshCw, 
  AlertCircle, 
  ChevronDown,
  Award,
  Zap,
  Info,
  Clock,
  Scale,
  Battery,
  Target,
  Activity
} from 'lucide-react';
import StyledCard from '../ui/StyledCard';
import GradientButton from '../ui/GradientButton';
import TrainingAnalyzer from './TrainingAnalyzer';
import { MLDiagnosticsHook } from './utils/MLDebugger';
import { MLMonitor } from './utils/MLMonitor';
import { debugLog } from '../../utils/logger';

/**
 * AITrainingAdvisor - Componente avanzato per l'analisi e i suggerimenti di allenamento
 * Versione migliorata con categorizzazione più precisa, insight approfonditi,
 * visualizzazioni interattive e suggerimenti più personalizzati
 */
const AITrainingAdvisor = () => {
  const { isDark } = useTheme();
  const { getAuthenticatedApi } = useAuth();
  const api = getAuthenticatedApi();
  
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState(null);
  const [workoutData, setWorkoutData] = useState([]);
  const [seriesData, setSeriesData] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [userProfile, setUserProfile] = useState(null);
  const [timeRange, setTimeRange] = useState('month'); // 'week', 'month', 'all'
  const [activeTab, setActiveTab] = useState('suggestions'); // 'suggestions', 'analysis', 'insights'
  const [trainingInsights, setTrainingInsights] = useState(null);
  const [debugMode, setDebugMode] = useState(false);
  
  // Carica i dati iniziali e il profilo utente
  useEffect(() => {
    loadUserProfile();
    loadTrainingData();
  }, [timeRange]);
  
  /**
   * Carica il profilo dell'utente per personalizzare i suggerimenti
   */
  const loadUserProfile = async () => {
    try {
      // Richiesta API per ottenere dati profilo
      const profileResponse = await api.get('/utente_profilo.php');
      let profileData = profileResponse.data;
      
      // Se non esistono alcune informazioni chiave, stimale in base ai dati di allenamento
      if (!profileData.experienceLevel) {
        const workoutsResponse = await api.get('/allenamenti.php');
        const workoutCount = workoutsResponse.data.filter(w => w.durata_totale !== null).length;
        
        if (workoutCount < 20) {
          profileData.experienceLevel = 'beginner';
        } else if (workoutCount < 100) {
          profileData.experienceLevel = 'intermediate';
        } else {
          profileData.experienceLevel = 'advanced';
        }
      }
      
      setUserProfile(profileData);
    } catch (error) {
      console.warn('Errore nel caricamento del profilo utente:', error);
      // Imposta un profilo di default
      setUserProfile({
        experienceLevel: 'beginner',
        goals: ['general_fitness'],
        trainingDays: 3
      });
    }
  };
  
  /**
   * Carica i dati di allenamento dal server con timeframe migliorato
   */
  const loadTrainingData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Carica gli allenamenti
      const workoutsResponse = await api.get('/allenamenti.php');
      
      // Filtra gli allenamenti completati e ordinali per data
      const completedWorkouts = workoutsResponse.data
        .filter(workout => workout.durata_totale !== null)
        .sort((a, b) => new Date(b.data_allenamento) - new Date(a.data_allenamento));
      
      // Filtra gli allenamenti in base all'intervallo di tempo selezionato con date dinamiche
      const filteredWorkouts = filterWorkoutsByDynamicTimeRange(completedWorkouts, timeRange);
      
      // Carica le serie per gli allenamenti filtrati (limita a 15 allenamenti per performance)
      const allSeries = [];
      const workoutsToAnalyze = filteredWorkouts.slice(0, 15);
      
      for (const workout of workoutsToAnalyze) {
        try {
          const seriesResponse = await api.get(`/serie_completate.php?allenamento_id=${workout.id}`);
          const seriesWithWorkoutId = seriesResponse.data.map(series => ({
            ...series,
            workout_id: workout.id,
            workout_date: workout.data_allenamento
          }));
          allSeries.push(...seriesWithWorkoutId);
        } catch (error) {
          console.warn(`Errore nel caricamento delle serie per l'allenamento ${workout.id}:`, error);
        }
      }
      
      setWorkoutData(filteredWorkouts);
      setSeriesData(allSeries);
      
      if (filteredWorkouts.length > 0 && allSeries.length > 0) {
        analyzeTrainingData(filteredWorkouts, allSeries);
        // Genera insight avanzati
        generateTrainingInsights(filteredWorkouts, allSeries);
      } else {
        setLoading(false);
      }
      
    } catch (err) {
      console.error('Errore nel caricamento dei dati di allenamento:', err);
      setError('Impossibile caricare i dati di allenamento. Riprova più tardi.');
      setLoading(false);
    }
  };
  
  /**
   * Filtra allenamenti con date dinamiche per timeframe più precisi
   * @param {Array} workouts - Array di allenamenti
   * @param {string} timeRange - Intervallo di tempo
   * @returns {Array} Allenamenti filtrati
   */
  const filterWorkoutsByDynamicTimeRange = (workouts, timeRange) => {
    const now = new Date();
    const cutoffDate = new Date();
    
    switch (timeRange) {
      case 'week':
        // Ultimi 7 giorni esatti
        cutoffDate.setDate(now.getDate() - 7);
        break;
      case 'month':
        // Ultimi 30 giorni esatti
        cutoffDate.setDate(now.getDate() - 30);
        break;
      case 'quarter':
        // Ultimi 90 giorni
        cutoffDate.setDate(now.getDate() - 90);
        break;
      case 'all':
      default:
        // Imposta una data molto indietro per prendere tutto
        cutoffDate.setFullYear(2000);
        break;
    }
    
    return workouts.filter(workout => {
      const workoutDate = new Date(workout.data_allenamento);
      return workoutDate >= cutoffDate;
    });
  };
  
  /**
   * Genera insight avanzati per l'allenamento
   * @param {Array} workouts - Array di allenamenti
   * @param {Array} series - Array di serie
   */
  const generateTrainingInsights = async (workouts, series) => {
    try {
      // Ottieni tutti i dati storici per confronto
      const allWorkoutsResponse = await api.get('/allenamenti.php');
      const allHistoricalWorkouts = allWorkoutsResponse.data
        .filter(workout => workout.durata_totale !== null);
      
      // Ottieni solo i dati storici precedenti all'intervallo di tempo corrente
      const oldestSelectedDate = getOldestDate(workouts);
      const historicalWorkouts = allHistoricalWorkouts.filter(workout => {
        const workoutDate = new Date(workout.data_allenamento);
        return workoutDate < oldestSelectedDate;
      });
      
      // Recupera serie storiche per confronto
      const historicalSeries = [];
      for (const workout of historicalWorkouts.slice(-10)) { // Limita ai 10 allenamenti storici più recenti
        try {
          const seriesResponse = await api.get(`/serie_completate.php?allenamento_id=${workout.id}`);
          const seriesWithWorkoutId = seriesResponse.data.map(s => ({
            ...s,
            workout_id: workout.id,
            workout_date: workout.data_allenamento
          }));
          historicalSeries.push(...seriesWithWorkoutId);
        } catch (error) {
          console.warn(`Errore nel caricamento delle serie storiche per l'allenamento ${workout.id}:`, error);
        }
      }
      
      // Genera insight avanzati utilizzando i metodi di TrainingAnalyzer
      const insights = {
        // Analisi della progressione
        progression: TrainingAnalyzer.analyzeExerciseProgress(series),
        
        // Analisi distribuzione carico e volume
        loadDistribution: TrainingAnalyzer.analyzeLoadDistribution(series),
        
        // Analisi della frequenza
        frequency: calculateFrequencyAnalysis(workouts),
        
        // Analisi dell'ultima sessione
        lastSession: workouts.length > 0 ? 
          TrainingAnalyzer.generateSessionInsights(
            series.filter(s => s.workout_id === workouts[0].id),
            historicalSeries
          ) : null,
        
        // Identificazione gap nel programma
        trainingGaps: TrainingAnalyzer.identifyTrainingGaps(workouts, series, userProfile)
      };
      
      setTrainingInsights(insights);
    } catch (error) {
      console.error('Errore nella generazione degli insight:', error);
    }
  };
  
  /**
   * Calcola l'analisi della frequenza di allenamento
   * @param {Array} workouts - Array di allenamenti
   * @returns {Object} Analisi della frequenza
   */
  const calculateFrequencyAnalysis = (workouts) => {
    if (!workouts.length) return null;
    
    // Ordina gli allenamenti per data
    const sortedWorkouts = [...workouts].sort((a, b) => 
      new Date(a.data_allenamento) - new Date(b.data_allenamento)
    );
    
    // Data primo e ultimo allenamento
    const firstDate = new Date(sortedWorkouts[0].data_allenamento);
    const lastDate = new Date(sortedWorkouts[sortedWorkouts.length - 1].data_allenamento);
    
    // Calcolala durata in giorni
    const daysDiff = Math.round((lastDate - firstDate) / (1000 * 60 * 60 * 24)) || 1;
    
    // Calcola le settimane
    const weeksDiff = Math.max(1, Math.round(daysDiff / 7));
    
    // Calcola frequenza settimanale
    const weeklyFrequency = parseFloat((workouts.length / weeksDiff).toFixed(1));
    
    // Conta allenamenti per giorno della settimana
    const weekdayDistribution = Array(7).fill(0);
    workouts.forEach(workout => {
      const date = new Date(workout.data_allenamento);
      weekdayDistribution[date.getDay()]++;
    });
    
    // Identifica i giorni più frequenti
    const mostFrequentDays = weekdayDistribution
      .map((count, day) => ({ day, count }))
      .sort((a, b) => b.count - a.count)
      .filter(d => d.count > 0)
      .slice(0, 3);
    
    // Converti indici giorni in nomi giorni
    const dayNames = ['Domenica', 'Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato'];
    const favoriteTrainingDays = mostFrequentDays.map(d => dayNames[d.day]);
    
    // Calcola se la frequenza è costante o variabile
    const intervals = [];
    for (let i = 1; i < sortedWorkouts.length; i++) {
      const current = new Date(sortedWorkouts[i].data_allenamento);
      const previous = new Date(sortedWorkouts[i-1].data_allenamento);
      intervals.push(Math.round((current - previous) / (1000 * 60 * 60 * 24)));
    }
    
    // Deviazione standard degli intervalli
    const avgInterval = intervals.length ? 
      intervals.reduce((sum, int) => sum + int, 0) / intervals.length : 0;
    const stdDev = intervals.length ? 
      Math.sqrt(intervals.reduce((sum, int) => sum + Math.pow(int - avgInterval, 2), 0) / intervals.length) : 0;
    
    // Considera consistente se stdDev < 2 giorni
    const isConsistent = stdDev < 2;
    
    return {
      totalWorkouts: workouts.length,
      periodDays: daysDiff,
      weeklyAverage: weeklyFrequency,
      favoriteTrainingDays,
      consistency: {
        isConsistent,
        stdDevDays: parseFloat(stdDev.toFixed(1)),
        avgIntervalDays: parseFloat(avgInterval.toFixed(1))
      },
      weekdayDistribution: weekdayDistribution.map((count, index) => ({
        day: dayNames[index],
        count
      }))
    };
  };
  
  /**
   * Trova la data più vecchia in un array di allenamenti
   * @param {Array} workouts - Array di allenamenti
   * @returns {Date} Data più vecchia
   */
  const getOldestDate = (workouts) => {
    if (!workouts.length) return new Date();
    
    return workouts.reduce((oldest, workout) => {
      const date = new Date(workout.data_allenamento);
      return date < oldest ? date : oldest;
    }, new Date());
  };
  /**
   * Analizza i dati di allenamento e genera suggerimenti personalizzati
   * @param {Array} workouts - Array di allenamenti
   * @param {Array} series - Array di serie
   */
  const analyzeTrainingData = async (workouts = workoutData, series = seriesData) => {
    try {
      setAnalyzing(true);
      
      // Genera suggerimenti personalizzati in base al profilo utente
      let generatedSuggestions = [];
      if (userProfile) {
        generatedSuggestions = TrainingAnalyzer.generatePersonalizedTrainingSuggestions(
          workouts, 
          series, 
          userProfile
        );
      } else {
        // Usa l'approccio tradizionale se non c'è un profilo utente
        const basicSuggestions = TrainingAnalyzer.generateTrainingSuggestions(workouts, series);
        const muscleSuggestions = TrainingAnalyzer.generateMuscleBalanceSuggestions(series);
        generatedSuggestions = [...basicSuggestions, ...muscleSuggestions];
      }
      
      // Analisi avanzata di recupero e potenziale sovrallenamento
      const recoverySuggestions = TrainingAnalyzer.analyzeRecoveryAndOvertraining(workouts, series);
      
      // Combinazione e ordinamento dei suggerimenti
      const allSuggestions = [...generatedSuggestions, ...recoverySuggestions];
      
      // Ordinamento per priorità
      const sortedSuggestions = sortSuggestionsByPriority(allSuggestions);
      
      // Limita a massimo 10 suggerimenti per non sovraccaricare l'utente
      setSuggestions(sortedSuggestions.slice(0, 10));
    } catch (error) {
      console.error('Errore durante l\'analisi dei dati:', error);
      setError('Impossibile generare suggerimenti al momento.');
    } finally {
      setAnalyzing(false);
      setLoading(false);
    }
  };

  /**
   * Ordina i suggerimenti per priorità
   * @param {Array} suggestions - Array di suggerimenti
   * @returns {Array} Suggerimenti ordinati
   */
  const sortSuggestionsByPriority = (suggestions) => {
    const priorityOrder = {
      'critical': 0,
      'high': 1,
      'medium': 2,
      'low': 3
    };
    
    return [...suggestions].sort((a, b) => {
      // Prima ordina per priorità
      const aPriority = priorityOrder[a.priority] ?? 99;
      const bPriority = priorityOrder[b.priority] ?? 99;
      
      if (aPriority !== bPriority) {
        return aPriority - bPriority;
      }
      
      // Se stessa priorità, ordina per quelli che dovrebbero essere mostrati
      if (a.shouldShowTip !== b.shouldShowTip) {
        return a.shouldShowTip ? -1 : 1;
      }
      
      // Default in caso di parità
      return 0;
    });
  };
  
  /**
   * Categorizza i suggerimenti per tipo
   * @param {Array} suggestions - Array di suggerimenti
   * @returns {Object} Suggerimenti categorizzati
   */
  const categorizzaSuggestions = (suggestions) => {
    return {
      plateau: suggestions.filter(s => s.type === 'plateau'),
      progress: suggestions.filter(s => s.type === 'progress'),
      muscleBalance: suggestions.filter(s => s.type === 'muscle_balance' || s.type === 'muscle_missing'),
      recovery: suggestions.filter(s => s.type === 'recovery' || s.type === 'recovery_urgent' || s.type === 'deload'),
      frequency: suggestions.filter(s => s.type === 'frequency'),
      periodization: suggestions.filter(s => s.type === 'periodization' || s.type === 'variation'),
      general: suggestions.filter(s => s.type === 'general' || !s.type)
    };
  };

  /**
   * Ottiene l'icona Lucide in base al tipo di suggerimento
   * @param {string} iconName - Nome dell'icona
   * @returns {JSX.Element} Componente Lucide
   */
  const getIconComponent = (iconName) => {
    const iconMap = {
      'TrendingUp': TrendingUp,
      'TrendingDown': TrendingDown,
      'RefreshCw': RefreshCw, 
      'BarChart2': BarChart2,
      'AlertCircle': AlertCircle,
      'Info': Info,
      'Scale': Scale,
      'Clock': Clock,
      'Award': Award,
      'Battery': Battery,
      'Target': Target,
      'Activity': Activity
    };
    
    const IconComponent = iconMap[iconName] || Info;
    return <IconComponent className={`h-5 w-5 mr-2 ${isDark ? 'text-white' : 'text-gray-700'}`} />;
  };

  /**
   * Renderizza una categoria di suggerimenti
   * @param {Array} suggestions - Suggerimenti da renderizzare
   * @param {string} title - Titolo della categoria
   * @param {string} defaultIcon - Icona predefinita
   * @returns {JSX.Element|null} Elemento JSX o null se non ci sono suggerimenti
   */
  const renderSuggestionCategory = (suggestions, title, defaultIcon) => {
    if (!suggestions || suggestions.length === 0) return null;
    
    return (
      <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
        <div className="flex items-center mb-3">
          {getIconComponent(defaultIcon)}
          <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-800'}`}>
            {title}
          </h3>
        </div>
        <div className="space-y-2">
          {suggestions.map((suggestion, index) => (
            <div 
              key={index} 
              className={`p-3 rounded-md ${
                isDark 
                  ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' 
                  : 'bg-white text-gray-800 hover:bg-gray-100'
              } transition-colors`}
            >
              <div className="flex items-start gap-2">
                {suggestion.icon && (
                  <div className="flex-shrink-0 mt-0.5">
                    {getIconComponent(suggestion.icon)}
                  </div>
                )}
                <div className="w-full">
                  <div className="flex justify-between items-start mb-1">
                    <h4 className="font-medium">{suggestion.title}</h4>
                    {/* Badge priorità */}
                    {suggestion.priority && (
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        suggestion.priority === 'critical' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' :
                        suggestion.priority === 'high' ? 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200' :
                        suggestion.priority === 'medium' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' :
                        'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200'
                      }`}>
                        {suggestion.priority === 'critical' ? 'Critico' :
                         suggestion.priority === 'high' ? 'Alta' :
                         suggestion.priority === 'medium' ? 'Media' : 'Bassa'}
                      </span>
                    )}
                  </div>
                  <p>{suggestion.description}</p>
                  
                  {/* Dati aggiuntivi specifici per tipo di suggerimento */}
                  {suggestion.type === 'progress' && suggestion.data && (
                    <div className="mt-2 text-sm">
                      <div className="flex items-center mb-1">
                        <Scale className="h-4 w-4 mr-1" />
                        <span className={isDark ? 'text-gray-300' : 'text-gray-600'}>
                          1RM Stimato: {suggestion.data.estimatedRM || suggestion.data.currentWeight}
                        </span>
                        <span className="mx-1">•</span>
                        <span className={isDark ? 'text-green-400' : 'text-green-600'}>
                          {suggestion.data.percentage}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-xs mt-1 text-gray-400">
                        <div>Peso max: {suggestion.data.maxWeight || suggestion.data.currentWeight}</div>
                        <div>Peso medio: {suggestion.data.averageWeight}</div>
                      </div>
                    </div>
                  )}
                  
                  {suggestion.type === 'plateau' && suggestion.data && (
                    <div className="mt-2 text-sm">
                      <div className="flex items-center mb-1">
                        <Scale className="h-4 w-4 mr-1" />
                        <span className={isDark ? 'text-gray-300' : 'text-gray-600'}>
                          1RM Stimato: {suggestion.data.estimatedRM || suggestion.data.currentWeight}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-xs mt-1">
                        <div className="text-gray-400">Peso max: {suggestion.data.maxWeight || suggestion.data.currentWeight}</div>
                        <div className="text-gray-400">Peso medio: {suggestion.data.averageWeight}</div>
                        <div className="col-span-2 text-blue-400">Consiglio: {suggestion.data.suggestedWeight}</div>
                      </div>
                      {/* Nuova visualizzazione strategie */}
                      {suggestion.data.breakThroughStrategies && (
                        <div className="mt-2 p-2 bg-blue-900/20 dark:bg-blue-900/40 rounded-md">
                          <h5 className="text-xs font-medium mb-1 text-blue-500 dark:text-blue-300">Strategie di superamento:</h5>
                          <ul className="list-disc list-inside text-xs text-gray-500 dark:text-gray-400">
                            {suggestion.data.breakThroughStrategies.map((strategy, i) => (
                              <li key={i}>{strategy}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {suggestion.type === 'muscle_balance' && suggestion.recommendations && (
                    <div className="mt-2 text-sm">
                      <ul className={`list-disc list-inside ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                        {suggestion.recommendations.slice(0, 2).map((rec, i) => (
                          <li key={i}>{rec}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {(suggestion.type === 'recovery' || suggestion.type === 'recovery_urgent' || suggestion.type === 'deload') && suggestion.recommendations && (
                    <div className="mt-2 text-sm">
                      <div className="flex items-center gap-1 mb-1 text-amber-500 dark:text-amber-400">
                        <Battery className="h-4 w-4" />
                        <span className="font-medium">Raccomandazioni per il recupero:</span>
                      </div>
                      <ul className="list-disc list-inside text-gray-500 dark:text-gray-400">
                        {suggestion.recommendations.slice(0, 2).map((rec, i) => (
                          <li key={i}>{rec}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  /**
   * Renderizza il tab per gli insight avanzati
   * @returns {JSX.Element} Componente con gli insight
   */
  const renderInsightsTab = () => {
    if (!trainingInsights) {
      return (
        <div className="flex justify-center items-center p-8">
          <div className="text-center">
            <AlertCircle className={`h-10 w-10 mx-auto mb-3 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
            <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'} mb-2`}>
              Dati insufficienti per generare insight avanzati.
            </p>
            <p className="text-sm text-gray-500">
              Continua ad allenarti e registrare i tuoi progressi per sbloccare questa funzionalità.
            </p>
          </div>
        </div>
      );
    }
    
    // Mostra insight di progressione
    const renderProgressionInsight = () => {
      const topProgressExercises = Object.entries(trainingInsights.progression)
        .filter(([_, data]) => parseFloat(data.weightChangePercent) > 0)
        .sort((a, b) => parseFloat(b[1].weightChangePercent) - parseFloat(a[1].weightChangePercent))
        .slice(0, 3);
      
      return (
        <div className={`p-4 rounded-lg mb-4 ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
          <h3 className={`font-semibold mb-3 ${isDark ? 'text-white' : 'text-gray-800'}`}>
            <div className="flex items-center">
              <TrendingUp className="h-5 w-5 mr-2 text-green-500" />
              Top Progressi
            </div>
          </h3>
          
          {topProgressExercises.length > 0 ? (
            <div className="space-y-3">
              {topProgressExercises.map(([exerciseName, data], index) => (
                <div 
                  key={index}
                  className={`p-3 rounded-md ${isDark ? 'bg-gray-700' : 'bg-white'}`}
                >
                  <div className="flex justify-between mb-1">
                    <span className={`font-medium ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                      {exerciseName}
                    </span>
                    <span className="text-green-500">+{data.weightChangePercent}%</span>
                  </div>
                  <div className="flex justify-between text-xs text-gray-400">
                    <span>Iniziale: {data.initialWeight}</span>
                    <span>Attuale: {data.currentWeight}</span>
                  </div>
                  {/* Barra di progresso */}
                  <div className="mt-2 h-2 bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-green-500 rounded-full"
                      style={{ width: `${Math.min(100, parseFloat(data.weightChangePercent) * 2)}%` }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500">
              Non sono stati rilevati progressi significativi. Continua ad allenarti!
            </p>
          )}
        </div>
      );
    };
    
    // Mostra frequenza di allenamento
    const renderFrequencyInsight = () => {
      const frequency = trainingInsights.frequency;
      
      if (!frequency) return null;
      
      return (
        <div className={`p-4 rounded-lg mb-4 ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
          <h3 className={`font-semibold mb-3 ${isDark ? 'text-white' : 'text-gray-800'}`}>
            <div className="flex items-center">
              <Clock className="h-5 w-5 mr-2 text-blue-500" />
              Frequenza di Allenamento
            </div>
          </h3>
          
          <div className="grid grid-cols-2 gap-4">
            <div className={`p-3 rounded-md ${isDark ? 'bg-gray-700' : 'bg-white'}`}>
              <div className="text-center">
                <div className="text-3xl font-bold text-blue-500 mb-1">{frequency.weeklyAverage}</div>
                <div className="text-xs text-gray-400">Allenamenti settimanali</div>
              </div>
            </div>
            
            <div className={`p-3 rounded-md ${isDark ? 'bg-gray-700' : 'bg-white'}`}>
              <div className="text-center">
                <div className={`text-3xl font-bold mb-1 ${
                  frequency.consistency.isConsistent ? 'text-green-500' : 'text-yellow-500'
                }`}>
                  {frequency.consistency.isConsistent ? 'Regolare' : 'Variabile'}
                </div>
                <div className="text-xs text-gray-400">Pattern di allenamento</div>
              </div>
            </div>
          </div>
          
          <div className={`mt-3 p-3 rounded-md ${isDark ? 'bg-gray-700' : 'bg-white'}`}>
            <div className="mb-2 text-sm font-medium text-gray-400">Giorni preferiti</div>
            <div className="flex flex-wrap gap-2">
              {frequency.favoriteTrainingDays.map((day, index) => (
                <span 
                  key={index}
                  className="px-2 py-1 text-xs rounded-full bg-blue-500/20 text-blue-500"
                >
                  {day}
                </span>
              ))}
            </div>
          </div>
        </div>
      );
    };
    
    // Mostra distribuzione carico
    const renderLoadDistributionInsight = () => {
      const loadData = trainingInsights.loadDistribution;
      
      if (!loadData || !loadData.muscleGroups) return null;
      
      // Trova il gruppo muscolare più allenato
      const muscleGroups = Object.entries(loadData.muscleGroups)
        .map(([name, data]) => ({ name, ...data }))
        .sort((a, b) => b.totalVolume - a.totalVolume);
      
      return (
        <div className={`p-4 rounded-lg mb-4 ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
          <h3 className={`font-semibold mb-3 ${isDark ? 'text-white' : 'text-gray-800'}`}>
            <div className="flex items-center">
              <BarChart2 className="h-5 w-5 mr-2 text-purple-500" />
              Distribuzione Volume
            </div>
          </h3>
          
          {muscleGroups.length > 0 ? (
            <div className="space-y-3">
              {muscleGroups.slice(0, 4).map((group, index) => (
                <div 
                  key={index}
                  className={`p-2 ${isDark ? 'bg-gray-700' : 'bg-white'} rounded-md`}
                >
                  <div className="flex justify-between mb-1">
                    <span className={`text-sm ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>{group.name}</span>
                    <span className={`text-xs ${
                      index === 0 ? 'text-green-500' : 
                      index === muscleGroups.length - 1 ? 'text-amber-500' : 'text-blue-500'
                    }`}>
                      {Math.round(group.totalVolume / muscleGroups[0].totalVolume * 100)}%
                    </span>
                  </div>
                  <div className="h-1.5 bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden">
                    <div 
                      className={`h-full rounded-full ${
                        index === 0 ? 'bg-green-500' : 
                        index === 1 ? 'bg-blue-500' : 
                        index === 2 ? 'bg-purple-500' :
                        'bg-amber-500'
                      }`}
                      style={{ width: `${(group.totalVolume / muscleGroups[0].totalVolume * 100)}%` }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500">
              Dati insufficienti per analizzare la distribuzione del volume.
            </p>
          )}
        </div>
      );
    };
    
    return (
      <div className="space-y-4">
        {renderProgressionInsight()}
        {renderFrequencyInsight()}
        {renderLoadDistributionInsight()}
        
        {/* Indice di fatica e raccomandazioni */}
        {trainingInsights.loadDistribution?.fatigueIndex > 0 && (
          <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
            <h3 className={`font-semibold mb-3 ${isDark ? 'text-white' : 'text-gray-800'}`}>
              <div className="flex items-center">
                <Battery className="h-5 w-5 mr-2 text-amber-500" />
                Indice di Fatica
              </div>
            </h3>
            
            <div className={`p-3 rounded-md ${isDark ? 'bg-gray-700' : 'bg-white'}`}>
              <div className="flex items-center">
                <div className="relative w-16 h-16 mr-4">
                  <div className="w-full h-full rounded-full overflow-hidden bg-gray-200 dark:bg-gray-600">
                    <div 
                      className={`h-full ${
                        trainingInsights.loadDistribution.fatigueIndex >= 8 ? 'bg-red-500' :
                        trainingInsights.loadDistribution.fatigueIndex >= 5 ? 'bg-amber-500' :
                        'bg-green-500'
                      }`}
                      style={{ width: `${trainingInsights.loadDistribution.fatigueIndex * 10}%` }}
                    ></div>
                  </div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-xl font-bold">{trainingInsights.loadDistribution.fatigueIndex}</span>
                  </div>
                </div>
                
                <div>
                  <div className={`font-medium ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                    {trainingInsights.loadDistribution.fatigueIndex >= 8 ? 'Rischio Sovrallenamento' :
                     trainingInsights.loadDistribution.fatigueIndex >= 5 ? 'Fatica Moderata' :
                     'Recupero Ottimale'}
                  </div>
                  <p className="text-sm text-gray-400">
                    {trainingInsights.loadDistribution.fatigueIndex >= 8 ? 'Considera una settimana di scarico' :
                     trainingInsights.loadDistribution.fatigueIndex >= 5 ? 'Monitora energia e performance' :
                     'Livelli di recupero adeguati'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };
  
  /**
   * Renderizza il tab per le raccomandazioni avanzate del programma
   * @returns {JSX.Element} Componente con raccomandazioni
   */
  const renderAnalysisTab = () => {
    if (!trainingInsights || !trainingInsights.trainingGaps) {
      return (
        <div className="flex justify-center items-center p-8">
          <div className="text-center">
            <AlertCircle className={`h-10 w-10 mx-auto mb-3 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
            <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'} mb-2`}>
              Dati insufficienti per l'analisi del programma.
            </p>
            <p className="text-sm text-gray-500">
              Continua ad allenarti per sbloccare l'analisi approfondita.
            </p>
          </div>
        </div>
      );
    }
    
    const { missingElements, suboptimalPatterns, programRecommendations } = trainingInsights.trainingGaps;
    
    return (
      <div className="space-y-4">
        {/* Elementi mancanti nel programma */}
        {missingElements.length > 0 && (
          <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
            <h3 className={`font-semibold mb-3 ${isDark ? 'text-white' : 'text-gray-800'}`}>
              <div className="flex items-center">
                <AlertCircle className="h-5 w-5 mr-2 text-amber-500" />
                Elementi Mancanti
              </div>
            </h3>
            
            <div className="space-y-2">
              {missingElements.map((element, index) => (
                <div 
                  key={index}
                  className={`p-3 rounded-md ${isDark ? 'bg-gray-700' : 'bg-white'}`}
                >
                  <div className="flex items-start gap-2">
                    <div className="mt-1">
                      <div className={`w-4 h-4 rounded-full ${
                        element.importance === 'high' ? 'bg-red-500' :
                        element.importance === 'medium' ? 'bg-amber-500' :
                        'bg-blue-500'
                      }`}></div>
                    </div>
                    <div>
                      <h4 className={`font-medium ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                        {element.title}
                      </h4>
                      <p className="text-sm text-gray-400">{element.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Pattern non ottimali */}
        {suboptimalPatterns.length > 0 && (
          <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
            <h3 className={`font-semibold mb-3 ${isDark ? 'text-white' : 'text-gray-800'}`}>
              <div className="flex items-center">
                <Activity className="h-5 w-5 mr-2 text-purple-500" />
                Pattern da Migliorare
              </div>
            </h3>
            
            <div className="space-y-2">
              {suboptimalPatterns.map((pattern, index) => (
                <div 
                  key={index}
                  className={`p-3 rounded-md ${isDark ? 'bg-gray-700' : 'bg-white'}`}
                >
                  <h4 className={`font-medium ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                    {pattern.title}
                  </h4>
                  <p className="text-sm text-gray-400">{pattern.description}</p>
                  
                  {pattern.suggestions && (
                    <div className="mt-2 text-xs">
                      <h5 className="font-medium text-purple-500 mb-1">Suggerimenti:</h5>
                      <ul className="list-disc list-inside text-gray-400">
                        {pattern.suggestions.map((suggestion, i) => (
                          <li key={i}>{suggestion}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Raccomandazioni per il programma */}
        {programRecommendations.length > 0 && (
          <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
            <h3 className={`font-semibold mb-3 ${isDark ? 'text-white' : 'text-gray-800'}`}>
              <div className="flex items-center">
                <Target className="h-5 w-5 mr-2 text-green-500" />
                Raccomandazioni Programma
              </div>
            </h3>
            
            <div className="space-y-2">
              {programRecommendations.map((rec, index) => (
                <div 
                  key={index}
                  className={`p-3 rounded-md ${isDark ? 'bg-gray-700' : 'bg-white'}`}
                >
                  <h4 className={`font-medium ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                    {rec.title}
                  </h4>
                  <p className="text-sm text-gray-400">{rec.description}</p>
                  
                  {rec.implementation && (
                    <div className="mt-2 p-2 bg-green-900/20 rounded-md">
                      <h5 className="text-xs font-medium text-green-500 mb-1">Come implementare:</h5>
                      <ul className="list-disc list-inside text-xs text-gray-400">
                        {rec.implementation.map((step, i) => (
                          <li key={i}>{step}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };
  // Rendering dello stato di caricamento
  if (loading) {
    return (
      <StyledCard className={`${isDark ? 'bg-gray-900 text-gray-100' : 'bg-white text-gray-900'} transition-colors duration-200`}>
        <StyledCard.Header>
          <StyledCard.Title>
            <div className="flex items-center gap-2">
              <Zap className={`h-5 w-5 ${isDark ? 'text-blue-400' : 'text-blue-600'} transition-colors`} />
              Consulente AI Allenamento
            </div>
          </StyledCard.Title>
          
          {/* Tabs di navigazione */}
          <div className="flex mt-2 border-b border-gray-200 dark:border-gray-700">
            <button
              onClick={() => setActiveTab('suggestions')}
              className={`px-4 py-2 text-sm font-medium transition-colors ${
                activeTab === 'suggestions'
                  ? 'text-blue-600 border-b-2 border-blue-600 dark:text-blue-400 dark:border-blue-400'
                  : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              Suggerimenti
            </button>
            <button
              onClick={() => setActiveTab('insights')}
              className={`px-4 py-2 text-sm font-medium transition-colors ${
                activeTab === 'insights'
                  ? 'text-blue-600 border-b-2 border-blue-600 dark:text-blue-400 dark:border-blue-400'
                  : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              Insight
            </button>
            <button
              onClick={() => setActiveTab('analysis')}
              className={`px-4 py-2 text-sm font-medium transition-colors ${
                activeTab === 'analysis'
                  ? 'text-blue-600 border-b-2 border-blue-600 dark:text-blue-400 dark:border-blue-400'
                  : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              Analisi Programma
            </button>
          </div>
          
          <div className="flex mt-2">
            <div className="inline-flex rounded-md shadow-sm" role="group">
              <button
                onClick={() => setTimeRange('week')}
                className={`px-3 py-1 text-xs font-medium rounded-l-lg border transition-colors ${
                  timeRange === 'week'
                    ? 'bg-blue-600 text-white border-blue-600'
                    : `${isDark ? 'bg-gray-800 text-gray-300 border-gray-600 hover:bg-gray-700' : 
                                'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`
                }`}
              >
                Ultima settimana
              </button>
              <button
                onClick={() => setTimeRange('month')}
                className={`px-3 py-1 text-xs font-medium border-t border-b transition-colors ${
                  timeRange === 'month'
                    ? 'bg-blue-600 text-white border-blue-600'
                    : `${isDark ? 'bg-gray-800 text-gray-300 border-gray-600 hover:bg-gray-700' : 
                                'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`
                }`}
              >
                Ultimo mese
              </button>
              <button
                onClick={() => setTimeRange('all')}
                className={`px-3 py-1 text-xs font-medium rounded-r-lg border transition-colors ${
                  timeRange === 'all'
                    ? 'bg-blue-600 text-white border-blue-600'
                    : `${isDark ? 'bg-gray-800 text-gray-300 border-gray-600 hover:bg-gray-700' : 
                                'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`
                }`}
              >
                Tutto
              </button>
            </div>
          </div>
        </StyledCard.Header>
        
        <StyledCard.Body>
          {analyzing ? (
            <div className="flex justify-center items-center p-4 gap-2">
              <div className={`animate-spin rounded-full h-8 w-8 border-b-2 ${isDark ? 'border-blue-400' : 'border-blue-600'} transition-colors`}></div>
              <p>Analisi in corso...</p>
            </div>
          ) : (
            <>
              {/* Tab Suggerimenti */}
              {activeTab === 'suggestions' && (
                suggestions.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Plateau */}
                    {renderSuggestionCategory(
                      categorizzaSuggestions(suggestions).plateau, 
                      'Plateau Rilevati', 
                      'RefreshCw'
                    )}
        
                    {/* Progressi */}
                    {renderSuggestionCategory(
                      categorizzaSuggestions(suggestions).progress, 
                      'Progressi', 
                      'TrendingUp'
                    )}
        
                    {/* Bilanciamento Muscolare */}
                    {renderSuggestionCategory(
                      categorizzaSuggestions(suggestions).muscleBalance, 
                      'Bilanciamento Muscolare', 
                      'BarChart2'
                    )}
                    
                    {/* Recupero e Sovrallenamento */}
                    {renderSuggestionCategory(
                      categorizzaSuggestions(suggestions).recovery, 
                      'Recupero e Sovrallenamento', 
                      'Battery'
                    )}
        
                    {/* Frequenza Allenamenti */}
                    {renderSuggestionCategory(
                      categorizzaSuggestions(suggestions).frequency, 
                      'Frequenza Allenamenti', 
                      'Clock'
                    )}
                    
                    {/* Periodizzazione */}
                    {renderSuggestionCategory(
                      categorizzaSuggestions(suggestions).periodization, 
                      'Periodizzazione e Variazione', 
                      'Target'
                    )}
        
                    {/* Suggerimenti Generali */}
                    {renderSuggestionCategory(
                      categorizzaSuggestions(suggestions).general, 
                      'Suggerimenti Generali', 
                      'Info'
                    )}
                  </div>
                ) : (
                  workoutData.length === 0 ? (
                    <div className={`text-center p-6 border border-dashed rounded-lg transition-colors ${
                      isDark 
                        ? 'border-gray-700 text-gray-300' 
                        : 'border-gray-200 text-gray-600'
                    }`}>
                      <AlertCircle className={`h-10 w-10 mx-auto mb-3 transition-colors ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
                      <p className="mb-2">Non ci sono allenamenti da analizzare nel periodo selezionato.</p>
                      <p className="text-sm">Completa alcuni allenamenti per ricevere consigli personalizzati.</p>
                    </div>
                  ) : (
                    <div className={`text-center p-6 border border-dashed rounded-lg transition-colors ${
                      isDark 
                        ? 'border-gray-700 text-gray-300' 
                        : 'border-gray-200 text-gray-600'
                    }`}>
                      <Award className={`h-10 w-10 mx-auto mb-3 transition-colors ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                      <p>
                        Stai progredendo bene! Al momento non ci sono suggerimenti specifici.
                      </p>
                    </div>
                  )
                )
              )}
              
              {/* Tab Insight */}
              {activeTab === 'insights' && renderInsightsTab()}
              
              {/* Tab Analisi Programma */}
              {activeTab === 'analysis' && renderAnalysisTab()}
            </>
          )}
          
          {/* Bottoni di controllo e diagnostica */}
          <div className="mt-4 text-right flex justify-end items-center gap-2">
            {process.env.NODE_ENV === 'development' && (
              <button
                onClick={() => setDebugMode(!debugMode)}
                className={`text-xs px-3 py-1 rounded border transition-colors ${
                  debugMode
                    ? `${isDark ? 'bg-purple-800 text-purple-200 border-purple-700' : 'bg-purple-100 text-purple-800 border-purple-300'}`
                    : `${isDark ? 'bg-gray-800 text-gray-300 border-gray-700' : 'bg-gray-100 text-gray-700 border-gray-300'}`
                }`}
              >
                {debugMode ? 'Disattiva Debug ML' : 'Attiva Debug ML'}
              </button>
            )}
            
            <div className={`mt-2 mb-2 text-xs transition-colors ${isDark ? 'text-gray-400' : 'text-gray-500'} text-right`}>
              Usa questo pulsante per aggiornare i consigli dopo aver completato nuovi allenamenti
            </div>
            <GradientButton 
              onClick={() => {
                setAnalyzing(true);
                setTimeout(() => {
                  analyzeTrainingData();
                  if (activeTab === 'insights' || activeTab === 'analysis') {
                    generateTrainingInsights(workoutData, seriesData);
                  }
                }, 100);
              }} 
              variant="primary"
              disabled={analyzing}
              className="flex items-center gap-2"
            >
              <RefreshCw className="h-4 w-4" />
              {analyzing ? 'Analisi in corso...' : 'Rigenera Analisi e Suggerimenti'}
            </GradientButton>
          </div>
          
          {/* Sezione di diagnostica ML (visibile solo in modalità debug) */}
          {debugMode && process.env.NODE_ENV === 'development' && (
            <div className="mt-6 p-4 border border-dashed rounded-lg bg-gray-900 border-purple-700">
              <h3 className="text-sm font-medium text-purple-300 mb-3">Diagnostica ML (Solo sviluppo)</h3>
              <MLDiagnosticsHook workouts={workoutData} sets={seriesData} userProfile={userProfile} />
              <div className="mt-4">
                <button
                  onClick={async () => {
                    setAnalyzing(true);
                    try {
                      const diagnosticResults = await TrainingAnalyzer.runMLDiagnostics(
                        workoutData, 
                        seriesData, 
                        userProfile
                      );
                      //debugLog('Risultati diagnostica ML:', diagnosticResults);
                      alert(`Diagnostica ML completata. ML attivo: ${diagnosticResults.isActive}. Controlla la console per dettagli.`);
                    } catch (error) {
                      console.error('Errore nella diagnostica ML:', error);
                    } finally {
                      setAnalyzing(false);
                    }
                  }}
                  className="text-xs px-3 py-1 bg-purple-700 text-purple-100 rounded hover:bg-purple-600 transition-colors"
                >
                  Esegui Diagnostica ML Completa
                </button>
              </div>
            </div>
          )}
        </StyledCard.Body>
      </StyledCard>
    );
};
};

export default AITrainingAdvisor;